import { useState } from "react";
import { FullScreenDialog } from "@/shared/ui/FullScreenDialog";
import { PrivacyPolicyBody } from "@/public/features/site/pages/PrivacyPage";
import type { OrgNewDraft } from "@/public/features/org/types";
import { CONSENTS } from "@/public/features/org/constants/orgNew";
import {
  Field,
  SectionHead,
  StepHeadLarge,
  isEmail,
} from "@/public/features/org/ui/orgNew/parts";

export function StepManager({ draft }: { draft: OrgNewDraft }) {
  const [policyOpen, setPolicyOpen] = useState(false);

  return (
    <>
      <section className="flex flex-col gap-3">
        <StepHeadLarge
          title="담당자 정보를 알려주세요."
          desc="문제와 매칭되면 입력한 연락처로 안내해 드려요."
        />
        <div className="flex flex-col gap-6">
          <Field
            label="담당자 이름"
            placeholder="이름을 입력해주세요."
            value={draft.managerName}
            onChange={draft.setManagerName}
            autoComplete="name"
          />
          <Field
            label="담당자 휴대폰 번호"
            placeholder="010-0000-0000"
            value={draft.managerPhone}
            onChange={draft.setManagerPhone}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            hint="해당 정보는 공개되지 않으며 관리자만 확인할 수 있습니다."
          />
          <Field
            label="담당자 이메일"
            placeholder="haebom@examle.com"
            value={draft.managerEmail}
            onChange={draft.setManagerEmail}
            type="email"
            inputMode="text"
            autoComplete="email"
            error={
              draft.managerEmail.length > 0 && !isEmail(draft.managerEmail)
                ? "이메일 형식이 맞지 않습니다."
                : null
            }
          />
        </div>
      </section>

      <section className="m-0 flex flex-col gap-3">
        <SectionHead
          title="수정용 비밀번호를 설정해 주세요."
          desc="등록 정보를 수정할 때 사용할 비밀번호예요."
        />
        <div className="flex flex-col gap-6">
          <Field
            label="비밀번호"
            placeholder="5자 이상, 영문과 숫자를 포함해 주세요."
            value={draft.password}
            onChange={draft.setPassword}
            type="password"
            autoComplete="new-password"
            error={draft.passwordMessage}
            success={
              draft.passwordMessage === null && draft.password.length > 0
                ? "사용 가능한 비밀번호입니다."
                : null
            }
          />
          <Field
            label="비밀번호 확인"
            placeholder="비밀번호를 다시 입력해 주세요."
            value={draft.passwordConfirm}
            onChange={draft.setPasswordConfirm}
            type="password"
            autoComplete="new-password"
            error={
              draft.hasConfirmError ? "비밀번호가 일치하지 않습니다." : null
            }
            success={
              !draft.hasConfirmError && draft.passwordConfirm.length > 0
                ? "비밀번호가 확인되었습니다."
                : null
            }
          />
        </div>
      </section>

      <section className="flex flex-col gap-3">
        {CONSENTS.map((c) => (
          <label
            key={c.key}
            className="ui-pressable flex cursor-pointer items-center gap-2"
          >
            <input
              type="checkbox"
              className="m-0 h-5 w-5 shrink-0 rounded accent-violet-500"
              checked={draft.consents.includes(c.key)}
              onChange={() => draft.toggleConsent(c.key)}
            />
            <span className="min-w-0 flex-1 shrink-0 grow basis-0 text-body-sm text-black-600">
              {c.label}
              {c.detail ? (
                <button
                  type="button"
                  className="ml-2 text-black-100 underline"
                  onClick={(e) => {
                    e.preventDefault();
                    setPolicyOpen(true);
                  }}
                >
                  자세히
                </button>
              ) : null}
            </span>
          </label>
        ))}
      </section>

      {policyOpen && (
        <FullScreenDialog
          title="개인정보 처리방침"
          onClose={() => setPolicyOpen(false)}
        >
          <PrivacyPolicyBody />
        </FullScreenDialog>
      )}
    </>
  );
}
